import { useEffect, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { api } from "@/lib/api";

// Worker bundled via CDN (unpkg) — robusto en producción con CRA.
// El método `new URL(...)` daba "Failed to fetch dynamically imported module" en build optimizado.
pdfjs.GlobalWorkerOptions.workerSrc =
  `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

/** Full-screen PDF/text viewer for the uploaded screenplay with page navigation + zoom. */
export default function ScriptViewer({ projectId, filename, onClose }) {
  const [meta, setMeta] = useState(null);
  const [textFallback, setTextFallback] = useState("");
  const [pdfBlob, setPdfBlob] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  const [numPages, setNumPages] = useState(0);
  const [scale, setScale] = useState(1.25);

  const isPdf = (filename || "").toLowerCase().endsWith(".pdf") ||
    (meta?.filename || "").toLowerCase().endsWith(".pdf");

  useEffect(() => {
    let cancelled = false;
    let createdUrl = null;
    (async () => {
      setLoading(true); setErr("");
      try {
        const tr = await api.get(`/icaa/projects/${projectId}/script/text`);
        if (cancelled) return;
        const m = { filename: tr.data.filename, char_count: tr.data.char_count, uploaded_at: tr.data.uploaded_at };
        setMeta(m);
        if ((m.filename || "").toLowerCase().endsWith(".pdf")) {
          const r = await api.get(`/icaa/projects/${projectId}/script/file`, { responseType: "blob" });
          if (cancelled) return;
          const blob = new Blob([r.data], { type: "application/pdf" });
          createdUrl = URL.createObjectURL(blob);
          setPdfBlob({ url: createdUrl, blob });
        } else {
          setTextFallback(tr.data.text || "");
        }
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || e.message);
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [projectId]);

  const downloadOriginal = () => {
    if (!pdfBlob) return;
    const a = document.createElement("a");
    a.href = pdfBlob.url;
    a.download = filename || meta?.filename || "guion.pdf";
    document.body.appendChild(a); a.click(); a.remove();
  };

  const openInNewTab = () => {
    if (pdfBlob) window.open(pdfBlob.url, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-[#2C2B29]" data-testid="script-viewer-modal">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-white/10 bg-[#2C2B29] px-4 py-3 text-white sm:px-6">
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-[0.35em] text-[#941620]">Guion subido</p>
          <p className="mt-0.5 truncate text-sm font-semibold">{meta?.filename || filename || "guion"}</p>
          {meta && (
            <p className="text-[11px] text-white/55">
              {(meta.char_count || 0).toLocaleString()} caracteres · subido {new Date(meta.uploaded_at).toLocaleString("es-ES")}
              {numPages > 0 && <> · {numPages} página{numPages === 1 ? "" : "s"}</>}
            </p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {isPdf && (
            <div className="inline-flex items-center gap-1 rounded-full bg-white/10 px-1 py-1 text-white" data-testid="viewer-zoom">
              <button onClick={() => setScale((s) => Math.max(0.5, +(s - 0.15).toFixed(2)))}
                      className="rounded-full px-2 py-0.5 text-xs hover:bg-white/15"
                      data-testid="viewer-zoom-out">−</button>
              <span className="min-w-[42px] text-center text-[11px] tabular-nums">{Math.round(scale * 100)}%</span>
              <button onClick={() => setScale((s) => Math.min(3, +(s + 0.15).toFixed(2)))}
                      className="rounded-full px-2 py-0.5 text-xs hover:bg-white/15"
                      data-testid="viewer-zoom-in">+</button>
            </div>
          )}
          {pdfBlob && (
            <button onClick={openInNewTab} data-testid="script-viewer-open-tab"
                    className="rounded-full bg-white/10 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-widest text-white hover:bg-white/20">
              ⇱ Abrir en pestaña
            </button>
          )}
          <button onClick={downloadOriginal} data-testid="script-viewer-download" disabled={!pdfBlob}
                  className="rounded-full bg-[#26775E] px-4 py-1.5 text-[11px] font-semibold uppercase tracking-widest text-white hover:bg-[#1c5c48] disabled:opacity-40">
            ↓ Descargar
          </button>
          <button onClick={onClose} data-testid="script-viewer-close"
                  className="rounded-full bg-white/10 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-widest text-white hover:bg-white/20">
            ✕ Cerrar
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-auto bg-[#33333f] py-6">
        {loading && (
          <div className="flex h-full items-center justify-center text-white/70" data-testid="viewer-loading">
            <p className="text-sm uppercase tracking-[0.3em]">Cargando guion…</p>
          </div>
        )}
        {!loading && err && (
          <div className="flex h-full items-center justify-center p-6">
            <p className="rounded-md bg-red-500/20 px-4 py-3 text-sm text-red-100">{err}</p>
          </div>
        )}
        {!loading && !err && pdfBlob && (
          <div className="mx-auto flex w-fit flex-col items-center gap-6" data-testid="script-viewer-pages">
            <Document
              file={pdfBlob.url}
              onLoadSuccess={({ numPages: n }) => setNumPages(n)}
              onLoadError={(e) => setErr(e?.message || "Error al cargar el PDF")}
              loading={<p className="text-sm text-white/60">Cargando páginas…</p>}>
              {Array.from({ length: numPages }, (_, i) => (
                <div key={`p-${i + 1}`} className="mb-5 shadow-2xl">
                  <Page pageNumber={i + 1} scale={scale}
                        renderTextLayer={true} renderAnnotationLayer={false} />
                </div>
              ))}
            </Document>
          </div>
        )}
        {!loading && !err && !pdfBlob && textFallback && (
          <div className="px-4 sm:px-8">
            <pre className="mx-auto max-w-3xl rounded-lg bg-[#FBFAF4] p-6 font-mono text-[13px] leading-relaxed text-stone-800 shadow-2xl"
                 style={{ whiteSpace: "pre-wrap" }}
                 data-testid="script-viewer-text">
{textFallback}
            </pre>
          </div>
        )}
      </div>
    </div>
  );
}
